import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useMemo, useState } from 'react';
import { Box, Paper, Group } from '@mantine/core';
import { CustomMention } from './CustomMention';
import './PromptEditor.css';
import SelectWithIcon from '@components/ui/Form/Select/SelectWithIcon';
import { useFormContext } from 'react-hook-form';

interface PromptEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  className?: string;
  videoOptions?: Array<{
    group: string;
    items: Array<{
      value: string;
      label: string;
      subtitle?: string;
      icon?: string;
      description?: string;
    }>;
  }>;
  audioOptions?: Array<{
    group: string;
    items: Array<{
      value: string;
      label: string;
      subtitle?: string;
      icon?: string;
      description?: string;
      videoId?: string;
    }>;
  }>;
  onVideoSelect?: (videoId: string | null) => void;
  onAudioSelect?: (audioId: string | null) => void;
}

export function PromptEditor({
  value = '',
  onChange,
  placeholder = 'Scrivi qui... Usa i menu sopra per inserire tag video e audio',
  className,
  videoOptions: externalVideoOptions,
  audioOptions: externalAudioOptions,
  onVideoSelect,
  onAudioSelect,
}: PromptEditorProps) {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [selectedAudio, setSelectedAudio] = useState<string | null>(null);
  const { setValue } = useFormContext();

  // Prepara i dati per i select - video (usa quelli esterni o default)
  const videoOptions = useMemo(
    () => externalVideoOptions ?? [],
    [externalVideoOptions]
  );

  // Prepara i dati per i select - audio (usa quelli esterni o default)
  const audioOptions = useMemo(
    () => externalAudioOptions ?? [],
    [externalAudioOptions]
  );

  const filteredAudioOptions = useMemo(() => {
    // If no video selected (null), return empty
    if (activeVideoId === null) {
      return [];
    }

    // If "Nessun video" is selected (empty string), show all audio options (standalone)
    if (activeVideoId === '') {
      return audioOptions.filter(group => group.items.length > 0);
    }

    // If a specific video is selected, filter audio for that video
    return audioOptions
      .map(group => ({
        group: group.group,
        items: group.items.filter(item => item.videoId === activeVideoId),
      }))
      .filter(group => group.items.length > 0);
  }, [audioOptions, activeVideoId]);

  const editor = useEditor({
    extensions: [StarterKit, CustomMention],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: 'prompt-editor-content',
        'data-placeholder': placeholder,
      },
    },
  });

  // Helper per trovare un item nelle opzioni
  const findItemInOptions = (
    id: string,
    options: Array<{
      group: string;
      items: Array<{
        value: string;
        label: string;
        icon?: string;
        description?: string;
        subtitle?: string;
      }>;
    }>
  ) => {
    for (const group of options) {
      const item = group.items.find(item => item.value === id);
      if (item) return item;
    }
    return null;
  };

  // Inserisci tag video nell'editor quando cambia la selezione
  useEffect(() => {
    if (!editor || !selectedVideo) return;

    const videoItem = findItemInOptions(selectedVideo, videoOptions);
    if (!videoItem) return;

    // Usa il comando insertContent con il formato mention, includendo l'icona e description
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'mention',
        attrs: {
          id: videoItem.value,
          label: videoItem.label,
          type: 'video',
          icon: videoItem.icon,
          description: videoItem.description || videoItem.subtitle || '',
        },
      })
      .insertContent(' ')
      .run();

    // Reset della selezione dopo l'inserimento
    setSelectedVideo(null);
    setValue('promptEditor.video', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    onVideoSelect?.(null);

    // Notifica il cambio
    const html = editor.getHTML();
    onChange?.(html);
  }, [selectedVideo, editor, videoOptions, onChange, onVideoSelect]);

  // Inserisci tag audio nell'editor quando cambia la selezione
  useEffect(() => {
    if (!editor || !selectedAudio) return;

    const audioItem = findItemInOptions(selectedAudio, audioOptions);
    if (!audioItem) return;

    // Usa il comando insertContent con il formato mention, includendo l'icona e description
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'mention',
        attrs: {
          id: audioItem.value,
          label: audioItem.label,
          type: 'audio',
          icon: audioItem.icon,
          description: audioItem.description || audioItem.subtitle || '',
        },
      })
      .insertContent(' ')
      .run();

    // Reset della selezione dopo l'inserimento
    setSelectedAudio(null);
    setValue('promptEditor.audio', null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    onAudioSelect?.(null);

    // Notifica il cambio
    const html = editor.getHTML();
    onChange?.(html);
  }, [selectedAudio, editor, audioOptions, onChange, onAudioSelect]);

  // Sync with form value when it changes externally
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  // Imposta le icone per i tag con data-tag-icon
  useEffect(() => {
    if (!editorContainerRef.current) return;

    const updateIcons = () => {
      const tags = editorContainerRef.current?.querySelectorAll(
        '.mention-tag-with-icon[data-tag-icon]'
      );
      tags?.forEach(tag => {
        const icon = tag.getAttribute('data-tag-icon');
        if (icon) {
          // Imposta lo stile inline usando CSS custom property
          (tag as HTMLElement).style.setProperty(
            '--tag-icon-url',
            `url(${icon})`
          );
        }
      });
    };

    // Aggiorna le icone quando cambia il contenuto
    const observer = new MutationObserver(updateIcons);
    if (editorContainerRef.current) {
      observer.observe(editorContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-tag-icon'],
      });
    }

    // Aggiorna immediatamente
    updateIcons();

    return () => {
      observer.disconnect();
    };
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <Box ref={editorContainerRef} className={className}>
      <Group gap='xs' mb='xs' grow wrap='nowrap' align='flex-end'>
        <SelectWithIcon
          name='promptEditor.video'
          placeholder='Seleziona video...'
          data={videoOptions}
          value={selectedVideo}
          onChange={value => {
            setSelectedVideo(value);
            // Set activeVideoId to empty string when "Nessun video" is selected, null otherwise
            setActiveVideoId(value === '' ? '' : (value ?? null));
            setSelectedAudio(null);
            setValue('promptEditor.video', value ?? null, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            setValue('promptEditor.audio', null, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            onVideoSelect?.(value ?? null);
          }}
          clearable
          searchable
          style={{ flex: 1 }}
        />
        <SelectWithIcon
          name='promptEditor.audio'
          placeholder={
            activeVideoId === ''
              ? 'Seleziona audio (standalone)'
              : activeVideoId
                ? 'Seleziona audio...'
                : 'Seleziona prima un video'
          }
          data={filteredAudioOptions}
          value={selectedAudio}
          onChange={value => {
            setSelectedAudio(value);
            setValue('promptEditor.audio', value ?? null, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            onAudioSelect?.(value ?? null);
          }}
          clearable
          searchable
          style={{ flex: 1 }}
          disabled={activeVideoId === null || filteredAudioOptions.length === 0}
        />
      </Group>
      <Paper withBorder p='xs' style={{ position: 'relative' }}>
        <EditorContent editor={editor} />
      </Paper>
    </Box>
  );
}
